import { useEffect } from 'react';
import { ArrowLeft, Clipboard, Copy, Home, RefreshCw, Scissors, Search, TextCursor } from 'lucide-react';
import { useContextMenu, type ContextMenuItem } from '@forq/ui';

/**
 * Globales FORQ-Rechtsklick-Menü fürs ganze ERP: unterdrückt das Browser-Kontextmenü ÜBERALL und
 * zeigt stattdessen ein FORQ-Menü. Zeilen/Elemente mit eigenem Kontextmenü (Kunden, Maschinen,
 * Artikel, Vorgänge, Angebote …) rufen preventDefault -- die werden hier bewusst NICHT überschrieben.
 * In Textfeldern erscheint ein Bearbeiten-Menü (Ausschneiden/Kopieren/Einfügen/Alles auswählen),
 * sonst ein Standard-Menü (Suche/Startseite/Zurück/Neu laden).
 *
 * Technik: ein Listener in der BUBBLE-Phase am document. React ruft für ein eigenes Zeilen-Menü
 * stopPropagation() (stoppt auch das native Event) -- damit erreicht dessen Rechtsklick diesen
 * Listener gar nicht erst; alles andere schon.
 */
type TextField = HTMLInputElement | HTMLTextAreaElement;

const TEXT_INPUT_TYPES = new Set(['text', 'search', 'email', 'tel', 'url', 'password', 'number', '']);

function editableFrom(target: EventTarget | null): HTMLElement | null {
  let element = target as HTMLElement | null;
  while (element && element !== document.body) {
    if (element instanceof HTMLTextAreaElement) return element;
    if (element instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(element.type)) return element;
    if (element.isContentEditable) return element;
    element = element.parentElement;
  }
  return null;
}

function isTextField(element: HTMLElement): element is TextField {
  return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;
}

async function pasteInto(restore: () => void): Promise<void> {
  try {
    const text = await navigator.clipboard.readText();
    if (!text) return;
    restore();
    document.execCommand('insertText', false, text);
  } catch {
    /* Zwischenablage nicht verfügbar/verweigert -- still ignorieren. */
  }
}

function editItems(element: HTMLElement): ContextMenuItem[] {
  const field = isTextField(element) ? element : null;
  // Auswahl JETZT festhalten (Rechtsklick verändert sie nicht) und beim Klick wiederherstellen --
  // sonst liegt der Fokus auf dem Menü-Knopf und execCommand hätte keine Auswahl.
  const start = field ? field.selectionStart ?? 0 : 0;
  const end = field ? field.selectionEnd ?? 0 : 0;
  const hasSelection = field ? end > start : (window.getSelection()?.toString().length ?? 0) > 0;
  const readOnly = field ? field.readOnly || field.disabled : element.getAttribute('contenteditable') === 'false';
  const restore = () => {
    element.focus();
    if (field) field.setSelectionRange(start, end);
  };
  return [
    { label: 'Ausschneiden', icon: <Scissors size={15} />, disabled: !hasSelection || readOnly, onSelect: () => { restore(); document.execCommand('cut'); } },
    { label: 'Kopieren', icon: <Copy size={15} />, disabled: !hasSelection, onSelect: () => { restore(); document.execCommand('copy'); } },
    { label: 'Einfügen', icon: <Clipboard size={15} />, disabled: readOnly, onSelect: () => { void pasteInto(restore); } },
    { label: 'Alles auswählen', icon: <TextCursor size={15} />, separatorBefore: true, onSelect: () => { element.focus(); if (field) field.select(); else document.execCommand('selectAll'); } },
  ];
}

export function GlobalContextMenu({ onSearch, onHome, onBack }: {
  readonly onSearch: () => void;
  readonly onHome: () => void;
  readonly onBack: () => void;
}) {
  const { openAt, menu } = useContextMenu();

  useEffect(() => {
    function handle(event: MouseEvent) {
      // Ein Element hat schon sein eigenes FORQ-Menü gezeigt (preventDefault) -> nicht überschreiben.
      if (event.defaultPrevented) return;
      event.preventDefault();
      const editable = editableFrom(event.target);
      const items: ContextMenuItem[] = editable ? editItems(editable) : [
        { label: 'Suche (Strg K)', icon: <Search size={15} />, onSelect: onSearch },
        { label: 'Startseite', icon: <Home size={15} />, onSelect: onHome },
        { label: 'Zurück', icon: <ArrowLeft size={15} />, onSelect: onBack },
        { label: 'Neu laden', icon: <RefreshCw size={15} />, separatorBefore: true, onSelect: () => window.location.reload() },
      ];
      openAt(event.clientX, event.clientY, items);
    }
    document.addEventListener('contextmenu', handle);
    return () => document.removeEventListener('contextmenu', handle);
  }, [openAt, onSearch, onHome, onBack]);

  return menu;
}
